import React, { useState, useEffect } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

const MultiRoomSelector = ({ 
  rooms = [], 
  selectedRoomIds = [], 
  onSelectionChange, 
  userName = '',
  disabled = false,
  className = '' 
}) => {
  const [internalSelection, setInternalSelection] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);

  // Synchroniser la sélection interne avec les props
  useEffect(() => {
    console.log('=== DEBUG MULTIROOMSELECTOR SYNC ===');
    console.log('selectedRoomIds prop received:', selectedRoomIds);
    console.log('Type of selectedRoomIds:', typeof selectedRoomIds);
    console.log('Is array:', Array.isArray(selectedRoomIds));
    
    const normalizedIds = selectedRoomIds.map(id => Number(id));
    console.log('Normalized IDs:', normalizedIds);
    setInternalSelection(normalizedIds);
    setSelectAll(normalizedIds.length === rooms.length && rooms.length > 0);
    console.log('======================================');
  }, [selectedRoomIds, rooms]);

  // Filtrer les salles selon le terme de recherche
  const filteredRooms = rooms.filter(room => 
    room.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Gérer la sélection/désélection d'une salle
  const handleRoomToggle = (roomId) => {
    if (disabled) return;
    
    const normalizedRoomId = Number(roomId);
    let updatedSelection;
    
    if (internalSelection.includes(normalizedRoomId)) {
      // Retirer la salle
      updatedSelection = internalSelection.filter(id => id !== normalizedRoomId);
    } else {
      // Ajouter la salle
      updatedSelection = [...internalSelection, normalizedRoomId];
    }
    
    setInternalSelection(updatedSelection);
    
    // Générer le titre automatiquement
    const selectedRoomNames = rooms
      .filter(room => updatedSelection.includes(Number(room.id)))
      .map(room => room.name)
      .sort()
      .join(', ');
    
    const newTitle = selectedRoomNames 
      ? `${userName} - Non disponible - ${selectedRoomNames}`
      : `${userName} - Non disponible`;
    
    // Notifier le parent du changement
    if (onSelectionChange) {
      onSelectionChange({
        roomIds: updatedSelection,
        title: newTitle,
        selectedRooms: rooms.filter(room => updatedSelection.includes(Number(room.id)))
      });
    }
  };

  // Gérer la sélection/désélection de toutes les salles
  const handleSelectAll = () => {
    if (disabled) return;
    
    let updatedSelection;
    
    if (selectAll) {
      // Désélectionner tout
      updatedSelection = [];
    } else {
      // Sélectionner toutes les salles filtrées
      updatedSelection = filteredRooms.map(room => Number(room.id));
    }
    
    setInternalSelection(updatedSelection);
    setSelectAll(!selectAll);
    
    // Générer le titre automatiquement
    const selectedRoomNames = rooms
      .filter(room => updatedSelection.includes(Number(room.id)))
      .map(room => room.name)
      .sort()
      .join(', ');
    
    const newTitle = selectedRoomNames 
      ? `${userName} - Non disponible - ${selectedRoomNames}`
      : `${userName} - Non disponible`;
    
    // Notifier le parent du changement
    if (onSelectionChange) {
      onSelectionChange({
        roomIds: updatedSelection,
        title: newTitle,
        selectedRooms: rooms.filter(room => updatedSelection.includes(Number(room.id)))
      });
    }
  };

  // Effacer la sélection
  const handleClearSelection = () => {
    if (disabled) return;
    
    setInternalSelection([]);
    setSelectAll(false);
    
    const newTitle = `${userName} - Non disponible`;
    
    if (onSelectionChange) {
      onSelectionChange({
        roomIds: [],
        title: newTitle,
        selectedRooms: []
      });
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Barre de recherche et actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Rechercher une salle..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>
        
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            disabled={disabled || filteredRooms.length === 0}
            className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {selectAll ? 'Désélectionner tout' : 'Sélectionner tout'}
          </button>
          
          {internalSelection.length > 0 && (
            <button
              type="button"
              onClick={handleClearSelection}
              disabled={disabled}
              className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Compteur de sélection */}
      <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
        <span className="font-medium">{internalSelection.length}</span> salle(s) sélectionnée(s) sur {rooms.length}
      </div>

      {/* Liste des salles */}
      <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md bg-white">
        {filteredRooms.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            {searchTerm ? 'Aucune salle trouvée pour cette recherche' : 'Aucune salle disponible'}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredRooms.map(room => {
              const isSelected = internalSelection.includes(Number(room.id));
              
              return (
                <label 
                  key={room.id} 
                  className={`flex items-center space-x-3 p-3 cursor-pointer transition-colors ${
                    disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-50'
                  } ${isSelected ? 'bg-blue-50' : ''}`}
                >
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleRoomToggle(room.id)}
                      disabled={disabled}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
                    />
                    {isSelected && (
                      <CheckIcon className="absolute inset-0 h-4 w-4 text-blue-600 pointer-events-none" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${
                        isSelected ? 'text-blue-900' : 'text-gray-900'
                      }`}>
                        {room.name}
                      </span>
                      
                      {room.capacity && (
                        <span className="text-xs text-gray-500 ml-2">
                          {room.capacity} places
                        </span>
                      )}
                    </div>
                    
                    {room.description && (
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        {room.description}
                      </p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Résumé de la sélection */}
      {internalSelection.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Salles sélectionnées :</h4>
          <div className="flex flex-wrap gap-1">
            {rooms
              .filter(room => internalSelection.includes(Number(room.id)))
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(room => (
                <span 
                  key={room.id}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                >
                  {room.name}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => handleRoomToggle(room.id)}
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 text-blue-600 hover:text-blue-800 focus:outline-none"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiRoomSelector;